import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, useAdminState, Erro } from "../../hooks/useAdminApi";

/** VISÃO GERAL — o pulso da operação. */
export default function Overview() {
  const { tema, setTema } = useOutletContext();
  const navigate = useNavigate();
  const { erro, rodar, limparErro } = useAdminState();

  const [stats, setStats] = useState(null);
  const [funil, setFunil] = useState(null);

  useEffect(() => {
    rodar(async () => {
      const [s, f] = await Promise.all([
        apiGet(API_ROUTES.ADMIN.stats()),
        apiGet(API_ROUTES.ADMIN.quizFunnel(30)).catch(() => null),
      ]);
      setStats(s);
      setFunil(f);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O backend devolve { total, pagos, gratuitos, assinaturasPorStatus }.
  const total = stats?.total ?? 0;
  const pagos = stats?.pagos ?? 0;
  const gratuitos = stats?.gratuitos ?? Math.max(0, total - pagos);
  const pct = (n) => (total > 0 ? `${((n / total) * 100).toFixed(1).replace(".", ",")}% da base` : "—");

  const cards = [
    { label: "Total de usuários", valor: total, pe: "todos os cadastros" },
    { label: "Pagantes", valor: pagos, pe: pct(pagos), cor: "var(--green)" },
    { label: "Gratuitos", valor: gratuitos, pe: pct(gratuitos), cor: "var(--gray)" },
    {
      label: "Conversão do funil",
      valor: funil?.conversao ?? "—",
      pe: funil ? `${funil.concluiram} de ${funil.iniciaram} que iniciaram` : "sem dados ainda",
      destaque: true,
    },
  ];

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Visão geral"
        subtitulo="O pulso da operação: base, receita e as últimas transações."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        {/* ⚠️ O NÚMERO QUE FALTAVA. Vem primeiro de propósito: é o que decide se o negócio
            está saudável ou se metade da base é fantasma. */}
        {stats?.uso && <UsoReal uso={stats.uso} pagos={pagos} />}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
            marginBottom: 22,
          }}
        >
          {cards.map((c) => (
            <div key={c.label} className="adm__card" style={{ padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                {c.cor && (
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.cor }} />
                )}
                <span style={{ fontSize: 12.5, color: "var(--text2)" }}>{c.label}</span>
              </div>
              <strong
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: -0.5,
                  color: c.destaque ? "var(--red)" : "var(--text)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {typeof c.valor === "number" ? c.valor.toLocaleString("pt-BR") : c.valor}
              </strong>
              <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>{c.pe}</div>
            </div>
          ))}
        </div>

        {/* Atalhos para o que provavelmente está pendente — em vez de um gráfico
            decorativo, aponta o trabalho que existe. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))", gap: 14 }}>
          <Atalho
            titulo="Curadoria de faixa etária"
            texto="A idade é a 1ª pergunta do funil — mas ela não filtra nada enquanto os conteúdos não tiverem faixa."
            botao="Ir para Conteúdos"
            onClick={() => navigate("/admin/conteudos")}
          />
          <Atalho
            titulo="Produtos das lojas"
            texto="Os product_ids da App Store e da Play Store precisam estar cadastrados para a compra ser reconhecida."
            botao="Ir para o Catálogo"
            onClick={() => navigate("/admin/catalogo")}
          />
          <Atalho
            titulo="Onde as pessoas desistem"
            texto="A única pergunta que importa num funil: em qual tela elas caem fora."
            botao="Ver o funil"
            onClick={() => navigate("/admin/funil")}
          />
        </div>
      </div>
    </>
  );
}

/**
 * USO REAL — quantos assinantes realmente abrem o produto.
 *
 * Por que este bloco existe e por que vem primeiro:
 *
 * "Pagantes" hoje significa apenas "já comprou alguma vez" — NADA nunca expira (1 de
 * 123.287 assinaturas tem data de vencimento), então quem parou de pagar em 2025 ainda
 * está contado. Estimando pelo tráfego do CDN, ~0,6–1,1% dos "pagantes" abrem o produto
 * num dia qualquer.
 *
 * O painel precisa dizer isso na cara — e precisa dizer DESDE QUANDO está medindo, senão
 * o "nunca voltaram" mente nos primeiros dias.
 */
function UsoReal({ uso, pagos }) {
  const medindoHa = uso.medindoDesde
    ? Math.max(
        1,
        Math.round((Date.now() - new Date(uso.medindoDesde)) / 86400000)
      )
    : 0;

  // Antes de ~7 dias medindo, "nunca voltaram" ainda inclui muita gente que só não
  // abriu o app nesses poucos dias. Dizer isso é mais honesto que mostrar um número
  // assustador e errado.
  const cedoDemais = medindoHa < 7;

  const pct = parseFloat(uso.pctPagantesAtivos30d) || 0;
  const critico = !cedoDemais && pagos > 0 && pct < 20;

  return (
    <div
      className="adm__card"
      style={{
        padding: "18px 22px",
        marginBottom: 16,
        borderColor: critico ? "var(--red)" : "var(--border)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <strong style={{ fontSize: 14 }}>Uso real</strong>
        <span style={{ fontSize: 11.5, color: "var(--text3)" }}>
          {uso.medindoDesde
            ? `medindo há ${medindoHa} ${medindoHa === 1 ? "dia" : "dias"}`
            : "ainda sem dados"}
        </span>
      </div>

      <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
        {[
          ["Ativos hoje", uso.hoje],
          ["Últimos 7 dias", uso.em7dias],
          ["Últimos 30 dias", uso.em30dias],
        ].map(([l, v]) => (
          <div key={l}>
            <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 3 }}>{l}</div>
            <strong style={{ fontSize: 24, fontVariantNumeric: "tabular-nums" }}>
              {Number(v).toLocaleString("pt-BR")}
            </strong>
          </div>
        ))}

        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 3 }}>
            dos pagantes, ativos em 30d
          </div>
          <strong
            style={{
              fontSize: 24,
              color: critico ? "var(--red)" : "var(--text)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {uso.pctPagantesAtivos30d}
          </strong>
        </div>
      </div>

      {cedoDemais && uso.medindoDesde && (
        <p style={{ fontSize: 12, color: "var(--text3)", margin: "12px 0 0", lineHeight: 1.55 }}>
          Ainda é cedo para confiar nestes números — estamos medindo há {medindoHa}{" "}
          {medindoHa === 1 ? "dia" : "dias"}. Quem não abriu o app nesse período ainda não
          é necessariamente inativo. Volte em uma semana.
        </p>
      )}

      {critico && (
        <p style={{ fontSize: 12.5, color: "var(--orange)", margin: "12px 0 0", lineHeight: 1.6 }}>
          ⚠️ <strong>Só {uso.pctPagantesAtivos30d} dos “pagantes” abriram o produto em 30 dias.</strong>{" "}
          Lembre que “pagante” hoje significa “já comprou alguma vez” — nada nunca expira,
          então quem parou de pagar continua contado aqui. Este número provavelmente está
          medindo uma base fantasma, não um problema de engajamento.
        </p>
      )}
    </div>
  );
}

function Atalho({ titulo, texto, botao, onClick }) {
  return (
    <div className="adm__card" style={{ padding: 18 }}>
      <strong style={{ fontSize: 14 }}>{titulo}</strong>
      <p style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6, margin: "7px 0 14px" }}>
        {texto}
      </p>
      <button className="adm__btnGhost" onClick={onClick}>
        {botao} →
      </button>
    </div>
  );
}
